import {
    DuplicateContactException,
    ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import AccountActionsAPI from '../../../api/accountActions';
import ContactAPI from '../../../api/contacts';
import AnalyticsHelper from '../../../helper/AnalyticsHelper';
import { parseAPIErrorMessage } from '../../../helper/APIHelper';
import { CONTACTS_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import types from '../../mutation-types';

const buildContactFormData = contactParams => {
  const formData = new FormData();
  const { additional_attributes = {}, ...contactProperties } = contactParams;
  Object.keys(contactProperties).forEach(key => {
    if (contactProperties[key] === undefined) return;

    formData.append(key, contactProperties[key] === null ? '' : contactProperties[key]);
  });
  const { social_profiles, ...additionalAttributesProperties } =
    additional_attributes;
  Object.keys(additionalAttributesProperties).forEach(key => {
    formData.append(
      `additional_attributes[${key}]`,
      additionalAttributesProperties[key]
    );
  });
  Object.keys(social_profiles).forEach(key => {
    formData.append(
      `additional_attributes[social_profiles][${key}]`,
      social_profiles[key]
    );
  });
  return formData;
};

export const raiseContactCreateErrors = error => {
  if (error.response?.status === 422) {
    throw new DuplicateContactException(error.response.data.attributes);
  } else if (error.response?.data?.message) {
    throw new ExceptionWithMessage(error.response.data.message);
  } else {
    throw new Error(parseAPIErrorMessage(error));
  }
};

const normalizeContactLabels = (labels = [], labelsCatalog = []) => {
  if (!Array.isArray(labels)) {
    return labels;
  }

  return labels
    .map(label => {
      if (typeof label === 'string') {
        const matchedLabel = labelsCatalog.find(
          catalogLabel => catalogLabel.title === label
        );
        return {
          title: label,
          color: matchedLabel?.color,
        };
      }

      const title = label?.title || label?.name;
      if (!title) {
        return null;
      }

      const matchedLabel = labelsCatalog.find(
        catalogLabel => catalogLabel.title === title
      );

      return {
        ...label,
        title,
        color: label?.color || matchedLabel?.color,
      };
    })
    .filter(Boolean);
};

const normalizeContactPayload = (contactPayload, labelsCatalog = []) => {
  if (!contactPayload || !Object.prototype.hasOwnProperty.call(contactPayload, 'labels')) {
    return contactPayload;
  }

  return {
    ...contactPayload,
    labels: normalizeContactLabels(contactPayload.labels, labelsCatalog),
  };
};

const buildKanbanContactUpdateData = (contactPayload, rootGetters = {}) => {
  if (!contactPayload) {
    return {};
  }

  const agents = rootGetters['agents/getAgents'] || [];
  const teams = rootGetters['teams/getTeams'] || [];
  const assignee = agents.find(
    agent => Number(agent.id) === Number(contactPayload.assignee_id)
  );
  const team =
    contactPayload.team ||
    teams.find(teamItem => Number(teamItem.id) === Number(contactPayload.team_id));

  return {
    title: contactPayload.name,
    contact_id: contactPayload.id,
    contact_avatar: contactPayload.thumbnail,
    contact_phone: contactPayload.phone_number,
    contact_email: contactPayload.email,
    assignee_id: contactPayload.assignee_id,
    assignee_name: assignee?.name || null,
    team_id: contactPayload.team_id,
    team_name: team?.name || null,
    custom_attributes: contactPayload.custom_attributes || {},
    labels: (contactPayload.labels || []).map(
      label => label.title || label.name || label
    ),
    bio: contactPayload.additional_attributes?.description || '',
    last_activity_at: contactPayload.last_activity_at
      ? new Date(contactPayload.last_activity_at * 1000).toISOString()
      : undefined,
    created_at: contactPayload.created_at
      ? new Date(contactPayload.created_at * 1000).toISOString()
      : undefined,
  };
};

export const actions = {
  search: async ({ commit }, { search, page, sortAttr, label }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ContactAPI.search(search, page, sortAttr, label);
      commit(types.CLEAR_CONTACTS);
      commit(types.SET_CONTACTS, payload);
      commit(types.SET_CONTACT_META, meta);
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    }
  },

  get: async ({ commit }, { page = 1, sortAttr, label } = {}) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ContactAPI.get(page, sortAttr, label);
      commit(types.CLEAR_CONTACTS);
      commit(types.SET_CONTACTS, payload);
      commit(types.SET_CONTACT_META, meta);
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    }
  },

  getWallet: async ({ commit }, { page = 1, sortAttr, scope = 'mine' } = {}) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ContactAPI.getWallet(page, sortAttr, scope);
      commit(types.CLEAR_CONTACTS);
      commit(types.SET_CONTACTS, payload);
      commit(types.SET_CONTACT_META, meta);
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    }
  },

  show: async ({ commit }, { id }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await ContactAPI.show(id);
      commit(types.SET_CONTACT_ITEM, response.data.payload);
      commit(types.SET_CONTACT_UI_FLAG, {
        isFetchingItem: false,
      });
      return response.data.payload;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, {
        isFetchingItem: false,
      });
      return null;
    }
  },

  update: async (
    { commit, rootGetters },
    { id, isFormData = false, ...contactParams }
  ) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      const response = await ContactAPI.update(
        id,
        isFormData ? buildContactFormData(contactParams) : contactParams
      );
      const labelsCatalog = rootGetters['labels/getLabels'] || [];
      commit(
        types.SET_CONTACT_ITEM,
        normalizeContactPayload(response.data.payload, labelsCatalog)
      );
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      emitter.emit(BUS_EVENTS.KANBAN_CARDS_REFRESH_REQUESTED, {
        scope: 'card',
        entityType: 'contact',
        cardId: `contact_${id}`,
        labelTitles: (response.data.payload?.labels || []).map(
          label => label.title || label.name || label
        ),
        updatedData: buildKanbanContactUpdateData(response.data.payload, rootGetters),
      });
      return response.data.payload;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      if (error.response?.status === 422) {
        throw new DuplicateContactException(error.response.data.attributes);
      } else {
        if (error instanceof Error) throw error;

        throw Object.assign(new Error(error.message || String(error)), error);
      }
    }
  },

  create: async ({ commit }, { isFormData = false, ...contactParams }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isCreating: true });
    try {
      const response = await ContactAPI.create(
        isFormData ? buildContactFormData(contactParams) : contactParams
      );

      AnalyticsHelper.track(CONTACTS_EVENTS.CREATE_CONTACT);
      commit(types.SET_CONTACT_ITEM, response.data.payload.contact);
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return response.data.payload.contact;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return raiseContactCreateErrors(error);
    }
  },

  delete: async ({ commit }, file) => {
    commit(types.SET_CONTACT_UI_FLAG, { isCreating: true });
    try {
      const response = await ContactAPI.deleteContacts(file);
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return response;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      }
      throw error;
    }
  },

  deleteSelectedContacts: async ({ commit }, contactIds) => {
    commit(types.SET_CONTACT_UI_FLAG, { isDeleting: true });
    try {
      await ContactAPI.deleteSelectedContacts(contactIds);
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      }
      throw error;
    }
  },

  bulkUpdate: async ({ commit }, params) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      await ContactAPI.bulkUpdate(params);
    } catch (error) {
      if (error.response?.data?.error) {
        throw new ExceptionWithMessage(error.response.data.error);
      }
      throw error;
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    }
  },

  deleteContact: async ({ commit }, id) => {
    commit(types.SET_CONTACT_UI_FLAG, { isDeleting: true });
    try {
      await ContactAPI.delete(id);
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(parseAPIErrorMessage(error));
      }
    }
  },

  export: async ({ commit }, { payload, label, column_names }) => {
    try {
      await ContactAPI.exportContacts({ payload, label, column_names });

      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      if (error.response?.data?.error === 'billing_limit_reached') {
        throw error;
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(parseAPIErrorMessage(error));
      }
    }
  },

  deleteCustomAttributes: async ({ commit }, { id, customAttributes }) => {
    try {
      const response = await ContactAPI.destroyCustomAttributes(
        id,
        customAttributes
      );
      commit(types.EDIT_CONTACT, response.data.payload);
    } catch (error) {
      throw new Error(parseAPIErrorMessage(error));
    }
  },

  deleteAvatar: async ({ commit }, id) => {
    try {
      const response = await ContactAPI.destroyAvatar(id);
      commit(types.EDIT_CONTACT, response.data.payload);
    } catch (error) {
      throw new Error(parseAPIErrorMessage(error));
    }
  },

  fetchContactableInbox: async ({ commit }, id) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetchingInboxes: true });
    try {
      const response = await ContactAPI.getContactableInboxes(id);
      const contact = {
        id,
        contactable_inboxes: response.data.payload,
      };
      commit(types.SET_CONTACT_ITEM, contact);
    } catch (error) {
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      } else {
        throw new Error(parseAPIErrorMessage(error));
      }
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isFetchingInboxes: false });
    }
  },

  updatePresence: ({ commit }, data) => {
    commit(types.UPDATE_CONTACTS_PRESENCE, data);
  },

  // Alimenta o CACHE de contatos, nao a listagem.
  //
  // `SET_CONTACT_ITEM` tambem faz `sortOrder.push` quando o id e novo, e
  // `sortOrder` E a lista renderizada em Contatos (`getContacts` e
  // `sortOrder.map`). Quem chama esta action e evento ao vivo -- conversa
  // criada, conversa atualizada, label mudada no kanban --, entao o push
  // anexava a listagem aberta um contato que o filtro ou a pagina atual nao
  // devolveriam: ele aparecia sozinho e sumia no F5. `EDIT_CONTACT` faz o
  // mesmo merge em `records` sem tocar em `sortOrder`, entao as outras telas
  // continuam encontrando o contato pelo `getContact`.
  setContact({ commit }, data) {
    commit(types.EDIT_CONTACT, data);
  },

  merge: async ({ commit }, { childId, parentId }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isMerging: true });
    try {
      const response = await AccountActionsAPI.merge(parentId, childId);
      commit(types.SET_CONTACT_ITEM, response.data);
    } catch (error) {
      throw new Error(parseAPIErrorMessage(error));
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isMerging: false });
    }
  },

  deleteContactThroughConversations: ({ commit }, id) => {
    commit(types.DELETE_CONTACT, id);
    commit(types.CLEAR_CONTACT_CONVERSATIONS, id, { root: true });
    commit(`contactConversations/${types.DELETE_CONTACT_CONVERSATION}`, id, {
      root: true,
    });
  },

  // Cache tambem, pelo mesmo motivo do `setContact` logo acima: o unico
  // chamador desta action e `onContactUpdate` (`helper/actionCable.js`), o
  // evento `contact.updated` -- e ele e transmitido para a conta inteira em
  // qualquer edicao de contato. Com `SET_CONTACT_ITEM`, editar o contato Y de
  // outra sessao, ou uma automacao mexendo num atributo dele, anexava Y a
  // listagem de Contatos aberta e filtrada.
  //
  // Isto nao entrava na repro da #738: `last_activity_at` esta em
  // `NON_EVENTFUL_UPDATE_ATTRIBUTES` (app/models/contact.rb), entao mensagem
  // recebida nao dispara `contact.updated`. E a mesma porta, por outro
  // caminho.
  updateContact: async ({ commit, rootGetters }, updateObj) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      const labelsCatalog = rootGetters['labels/getLabels'] || [];
      commit(
        types.EDIT_CONTACT,
        normalizeContactPayload(updateObj, labelsCatalog)
      );
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    }
  },

  bulkAssign: async (_, payload) => {
    try {
      await ContactAPI.bulkAssign(payload);
    } catch (error) {
      throw error;
    }
  },

  filter: async (
    { commit },
    { page = 1, sortAttr, queryPayload, resetState = true } = {}
  ) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ContactAPI.filter(page, sortAttr, queryPayload);
      if (resetState) {
        commit(types.CLEAR_CONTACTS);
        commit(types.SET_CONTACTS, payload);
        commit(types.SET_CONTACT_META, meta);
        commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
      }
      return payload;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    }
    return [];
  },

  setContactFilters({ commit }, data) {
    commit(types.SET_CONTACT_FILTERS, data);
  },

  clearContactFilters({ commit }) {
    commit(types.CLEAR_CONTACT_FILTERS);
  },

  // Action para refresh de um contato específico
  refreshContact: async ({ commit, dispatch }, contactId) => {
    try {
      const response = await ContactAPI.show(contactId);
      commit(types.SET_CONTACT_ITEM, response.data.payload);
      
      // Também atualizar as labels do contato
      dispatch('contactLabels/get', contactId, { root: true });
      
    } catch (error) {
      console.error('Erro ao atualizar contato:', error);
    }
  },
};
